import { paths, definitions } from './__generated__/schema.ts';
import { CompanyInfo, Schedule } from '@/api/customTypes.ts';

type ReplaceBracesWithColon<T extends string> =
  T extends `${infer Start}{${infer Param}}${infer Rest}`
    ? `${Start}:${Param}${ReplaceBracesWithColon<Rest>}`
    : T;

type ValidPaths<T extends paths = paths> = ReplaceBracesWithColon<
  keyof T & string
>;

const typed = <T extends Record<string, ValidPaths>>(r: T) => r;
const untyped = <T extends Record<string, string>>(r: T) => r;

export const Routes = {
  ...typed({
    QrPositions: '/manager/api/v0/property/qr-positions/',
    CompanyInfo: '/manager/api/v0/company-info/',
    LostHours: '/manager/api/v0/lost_hours/list/',
    LostHoursEmployees: '/manager/api/v0/lost_hours/employees/',
    CreateLostHours: '/manager/api/v0/lost_hours/',
    DeleteLostHours: '/manager/api/v0/lost_hours/delete/',
    ScheduleList: '/manager/api/v0/schedule/list/',
    UpdateSchedule: '/manager/api/v0/schedule/update/list/',
    RepeatSchedule:
      '/manager/api/v0/schedule/:employment_pk/:schedule_pk/repeat/',
    SendSchedule: '/manager/api/v0/schedule/sent/:schedule_pk/',
    SendAllSchedule: '/manager/api/v0/schedule/send_all/',
    ShiftDay: '/manager/api/v0/employee/shift-day/',
    EmployeeList: '/manager/api/v0/employee/list/',
    EmployeeCreate: '/manager/api/v0/employee/create/',
    EmployeeUpdate: '/manager/api/v0/employee/update/:employee_pk/',
    EmployeeBlock: '/manager/api/v0/employee/block/:employee_pk/',
    EmployeeComments: '/manager/api/v0/employee/comments/:employee_pk/',
    EmployeeTracking: '/manager/api/v0/employee/tracking/',
    Report: '/manager/api/v0/report/',
    ReviewReport: '/manager/api/v0/report/review/',
    InhouseInterview: '/manager/inhouse/interview/',
    AIInvoicesList: '/manager/api/v0/ai-invoices/',
    AIInvoiceDetail: '/manager/api/v0/ai-invoices/:id/',
    AIInvoicePaymentSession: '/manager/api/v0/ai-invoices/:id/payment-session/',
    Subscription: '/manager/api/v0/subscriptions/',
  }),
  ...untyped({
    // untyped paths (not present in generated schema)
    InterviewAction: '/manager/interview/action/',
    InterviewViewed: '/manager/interview/viewed/',
    LeadsList: '/manager/leads/:type/', // type = inhouse | by-agency
    EmployeeAction: '/manager/api/v0/employee/action/',
    ContactSales: '/manager/api/v0/billing/contact-sales/',
  }),
} as const;

export type Route = (typeof Routes)[keyof typeof Routes];

export type GetCompanyInfoParams =
  paths['/manager/api/v0/company-info/']['get']['parameters'];
// TODO: replace with paths['/manager/api/v0/company-info/']['get']['responses']['200']['schema'];
export type GetCompanyInfoResponse = CompanyInfo;

export type GetLostHoursParams =
  paths['/manager/api/v0/lost_hours/list/']['get']['parameters'];
export type GetLostHoursResponse =
  paths['/manager/api/v0/lost_hours/list/']['get']['responses']['200']['schema'];
export type LostHours = definitions['LostHoursGet'];
export type Lead = definitions['Lead'];

export type CreateLostHoursPayload =
  paths['/manager/api/v0/lost_hours/']['post']['parameters']['body']['data'];
export type CreateLostHoursResponse =
  paths['/manager/api/v0/lost_hours/']['post']['responses']['200'];

export type DeleteLostHoursPayload =
  paths['/manager/api/v0/lost_hours/delete/']['post']['parameters']['body']['data'];
export type DeleteLostHoursResponse =
  paths['/manager/api/v0/lost_hours/delete/']['post']['responses']['200'];

export type GetLostHoursEmployeeListResponse =
  paths['/manager/api/v0/lost_hours/employees/']['get']['responses']['200'];

export type GetScheduleListParams =
  paths['/manager/api/v0/schedule/list/']['get']['parameters']['query'];
// export type GetScheduleListResponse =
//   paths['/manager/api/v0/schedule/list/']['get']['responses']['200']['schema'];
// TODO: remove override here when it's fixed in swagger
export type GetScheduleListResponse = {
  items: Schedule[];
  pdf_url: string;
};

export type UpdateScheduleSchedulePayload =
  paths['/manager/api/v0/schedule/update/list/']['put']['parameters']['body']['data'];
export type UpdateScheduleScheduleResponse =
  paths['/manager/api/v0/schedule/update/list/']['put']['responses']['200'];

export type RepeatScheduleScheduleParams =
  paths['/manager/api/v0/schedule/{employment_pk}/{schedule_pk}/repeat/']['put']['parameters']['path'];
export type RepeatScheduleSchedulePayload =
  paths['/manager/api/v0/schedule/{employment_pk}/{schedule_pk}/repeat/']['put']['parameters']['body']['data'];
export type RepeatScheduleScheduleResponse =
  paths['/manager/api/v0/schedule/{employment_pk}/{schedule_pk}/repeat/']['put']['responses']['200']['schema'];

export type SendScheduleScheduleParams =
  paths['/manager/api/v0/schedule/sent/{schedule_pk}/']['put']['parameters']['path'];
export type SendScheduleScheduleResponse =
  paths['/manager/api/v0/schedule/sent/{schedule_pk}/']['put']['responses']['200'];

// export type SendAllScheduleSchedulePayload =
//   paths['/manager/api/v0/schedule/send_all/']['put']['parameters']['body']['data'];
// TODO: remove override here when it's fixed in swagger
export type SendAllScheduleSchedulePayload = {
  year: number;
  week: number;
};
export type SendAllScheduleScheduleResponse =
  paths['/manager/api/v0/schedule/send_all/']['put']['responses']['200'];

export type GetShiftDayEmployeeParams =
  paths['/manager/api/v0/employee/shift-day/']['get']['parameters']['query'];
export type GetShiftDayEmployeeResponse =
  paths['/manager/api/v0/employee/shift-day/']['get']['responses']['200']['schema'];
export type ShiftDayEmployee = definitions['GetSMSFirstDay'];

export type CreateShiftDayPayload =
  paths['/manager/api/v0/employee/shift-day/']['post']['parameters']['body']['data'];
export type CreateShiftDayResponse =
  paths['/manager/api/v0/employee/shift-day/']['post']['responses']['200'];

export type Employment = definitions['EmploymentSerializerMU'];
export type Employee = definitions['EmployeeSerializerMU'];
export type Position = definitions['PositionSerializerMU'];

export type GetEmployeeListParams =
  paths['/manager/api/v0/employee/list/']['get']['parameters'];
export type GetEmployeeListResponse =
  paths['/manager/api/v0/employee/list/']['get']['responses']['200']['schema'];

export type CreateEmployeePayload =
  paths['/manager/api/v0/employee/create/']['post']['parameters']['body']['data'];
export type CreateEmployeeResponse =
  paths['/manager/api/v0/employee/create/']['post']['responses']['200'];

export type UpdateEmployeeParams =
  paths['/manager/api/v0/employee/update/{employee_pk}/']['post']['parameters']['path'];
export type UpdateEmployeePayload =
  paths['/manager/api/v0/employee/update/{employee_pk}/']['post']['parameters']['body']['data'];
export type UpdateEmployeeResponse =
  paths['/manager/api/v0/employee/update/{employee_pk}/']['post']['responses']['200'];

export type BlockEmployeeParams =
  paths['/manager/api/v0/employee/block/{employee_pk}/']['post']['parameters']['path'];
export type BlockEmployeePayload =
  paths['/manager/api/v0/employee/block/{employee_pk}/']['post']['parameters']['body']['data'];
export type BlockEmployeeResponse =
  paths['/manager/api/v0/employee/block/{employee_pk}/']['post']['responses']['200'];

export type EmployeeComment = definitions['EmployeeComment'];
export type GetEmployeeCommentsParams =
  paths['/manager/api/v0/employee/comments/{employee_pk}/']['get']['parameters']['path'];
export type GetEmployeeCommentsResponse =
  paths['/manager/api/v0/employee/comments/{employee_pk}/']['get']['responses']['200']['schema'];

export type CreateEmployeeCommentParams =
  paths['/manager/api/v0/employee/comments/{employee_pk}/']['post']['parameters']['path'];
export type CreateEmployeeCommentPayload = Partial<
  paths['/manager/api/v0/employee/comments/{employee_pk}/']['post']['parameters']['body']['data']
>;
export type CreateEmployeeCommentResponse =
  paths['/manager/api/v0/employee/comments/{employee_pk}/']['post']['responses']['200'];

export type EmployeeTracking = definitions['EmployeeTrackingGetResponse'];
export type WorkingHour = definitions['WorkingHour'];
export type GetEmployeeTrackingParams =
  paths['/manager/api/v0/employee/tracking/']['get']['parameters']['query'];
export type GetEmployeeTrackingResponse =
  paths['/manager/api/v0/employee/tracking/']['get']['responses']['200']['schema'];

export type GetReportParams =
  paths['/manager/api/v0/report/']['get']['parameters']['query'];
export type GetReportResponse =
  paths['/manager/api/v0/report/']['get']['responses']['200']['schema'];

export type ReviewReportPayload =
  paths['/manager/api/v0/report/review/']['post']['parameters']['body']['data'];
export type ReviewReportResponse =
  paths['/manager/api/v0/report/review/']['post']['responses']['200'];

export type CreateInterviewPayload =
  paths['/manager/inhouse/interview/']['post']['parameters']['body']['data'];
export type CreateInterviewResponse =
  paths['/manager/inhouse/interview/']['post']['responses']['200'];

export type GetQrPositionsResponse =
  paths['/manager/api/v0/property/qr-positions/']['get']['responses']['200']['schema'];
export type UpdateQrPositionsPayload =
  paths['/manager/api/v0/property/qr-positions/']['put']['parameters']['body']['data'];

export type AIInvoice = definitions['AIInvoice'];
export type GetAIInvoicesResponse =
  paths['/manager/api/v0/ai-invoices/']['get']['responses']['200']['schema'];
export type AIInvoicePaymentSession = definitions['AIInvoicePaymentSession'];

export type Subscription = definitions['Subscription'];

export interface ContactSalesPayload {
  name: string;
  email: string;
  phone?: string;
  message?: string;
}

export interface ContactSalesResponse {
  success: boolean;
}

export * from '@/api/customTypes.ts';
